import { Injectable } from '@angular/core';
import { GeneralService } from '../services/general.service';
import * as moment from 'moment';
import { Observable, Observer } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GeneralUtils {
  constructor(private generalService: GeneralService) { }

  /**
   * Convierte formato de fecha
   *
   * @param fechaMoment en formato "moment"
   * @return fecha en formato 'Thu Nov 26 2020 00:00:00 GMT-0300 (-03)'
   */
  convierteFechaParaAlmacenar(fechaMoment): string {
    const formatoFechaLocal = moment(fechaMoment).local().format(); // retorna "2020-11-26T00:00:00-03:00"
    return new Date(formatoFechaLocal).toString();
  }

  convierteFechaParaMostrar(fecha: string, dias = null): string {
    const date = moment(fecha);
    if (dias) {
      date.add(dias, 'days');
    }
    return fecha ? date.format('DD/MM/YYYY') : '';
  }
  convierteFechaHoraParaMostrar(fecha: string): string {
    return moment(fecha).format('DD/MM/YYYY HH:mm');
  }
  convierteFechaParaSuseso(fecha): string {
    return moment(fecha).format('DD/MM/YYYY');
  }
  
  convierteFecha(fecha): string {
    return new Date(fecha).toDateString();
  }

  /**
   * Calcula edad de acuerdo a la fecha actual y fecha de nacimiento
   *
   * @param fechaDeNacimiento en formato DD/MM/AAAA
   * @return cantidad de años
   */
  calculaEdad(fechaDeNacimiento: string, inputFormat: string | undefined = '') {
    let diff;
    if (inputFormat) {
      diff = moment(fechaDeNacimiento, inputFormat).diff(moment(), 'milliseconds');
    } else {
      diff = moment(fechaDeNacimiento).diff(moment(), 'milliseconds');
    }
    const duration = moment.duration(diff);
    this.generalService.log('años', duration.years(), Math.abs(duration.years()));
    return Math.abs(duration.years());
  }


  /**
   * Convierte bytes a una representación corta y mas leible
   *
   * @param bytes número de bytes de archivo
   * @return texto con nomenclatura acortada
   */
  bytesToSize(bytes: number): string {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) {
      return '0 Byte';
    }
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const decimal = bytes / Math.pow(1024, i);
    return Math.round(decimal * 10) / 10 + ' ' + sizes[i];
  }

  /**
   * Genera imagen en base 64
   *
   * @param url - Ruta de la imagen
   * @returns cadena de imagen en base 64
   */
  getBase64ImageFromURL(url: string) {
    return new Observable((observer: Observer<string>) => {
      // create an image object
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.src = url;
      if (!img.complete) {
        // This will call another method that will create image from url
        img.onload = () => {
          observer.next(this.getBase64Image(img));
          observer.complete();
        };
        img.onerror = (err) => {
          observer.error(err);
        };
      } else {
        observer.next(this.getBase64Image(img));
        observer.complete();
      }
    });
  }

  getBase64Image(img: HTMLImageElement) {
    // We create a HTML canvas object that will create a 2d image
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    // This will draw image
    ctx.drawImage(img, 0, 0);
    // Convert the drawn image to Data URL
    const dataURL = canvas.toDataURL('image/png');
    return dataURL.replace(/^data:image\/(png|jpg);base64,/, '');
  }

  /**
   * Convierte un archivo PDF almacenado en una URL a string base64 (plain text)
   *
   * @param filepath - URL del archivo PDF
   * @param [filename='archivo.pdf'] - Nombre del archivo
   * @returns texto plano en base64
   */
  async fileToBase64(filepath, filename = 'archivo.pdf'): Promise<string> {
    const response = await fetch(filepath);
    const data = await response.blob();

    return new Promise((resolve) => {
      const file = new File([data], filename, { type: 'application/pdf' });
      const reader = new FileReader();

      // Convert data to base64
      reader.readAsDataURL(file);

      // Read file content on file loaded event
      reader.onload = (event) => {
        const codificacion = String(event.target.result);
        resolve(codificacion.split('data:application/pdf;base64,')[1]);
      };
    });
  }

  /**
   * Convierte un string base64 a un objeto blob
   * 
   * @param base64 - texto plano en base64
   * @param mime - tipo de archivo. Por defecto application/pdf
   * @returns objeto blob
   * 
   * SDO modifica versión original 20250729:
   * async base64ToBlob(base64: string, mime: string = 'application/pdf') {
   *  const base64Response = await fetch(data:${mime};base64,${base64});
   *  const blob = await base64Response.blob();
   *  return blob;
   * }
   * 
   * 
   */
    base64ToBlob(base64: string, mime: string = 'application/pdf'): Blob {
      const byteCharacters = atob(base64);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      return new Blob(byteArrays, { type: mime });
    }

  /**
   * Agrega ceros adelante al RUT (sin dígito verificador)
   *
   * @param rutSdv - Rut sin digito verificador
   * @returns RUT con ceros adelante (extensión de 10)
   */
  makeRun10Digitos(rutSdv: string): string {
    return rutSdv.toString().padStart(10, '0');
  }
}
